// Client-side bulk assignment of parameters to SVG diagram regions.
// Pure functions — no React, no network. The parsed result merges into the
// existing svgSections draft and is saved via the normal PUT /overview-config.

/**
 * Parse bulk CSV text into a merged svgSections array.
 *
 * CSV columns: `section_id, parameter_data_key`. One row per region-parameter
 * pair. `section_id` must be a region of the chosen template; `parameter_data_key`
 * is a parameter `dataKey` (sensor tag) — admins know tags, not internal UUIDs.
 *
 * Returns { svgSections, success, failed, errors }:
 *  - svgSections: existing assignments merged with valid new rows (deduped)
 *  - success: count of valid rows applied
 *  - failed: count of skipped rows
 *  - errors: [{ row, message }] for each skipped row (1-based incl. header)
 *
 * @param {string}   text          raw CSV
 * @param {object[]} allParams     deployment parameters (need id + dataKey)
 * @param {object[]} templateSections  template.sections ([{id,label}])
 * @param {object[]} existing      current svgSections ([{sectionId,parameterIds}])
 */
export function parseBulkSvgSections(text, allParams, templateSections, existing = []) {
  const lines = String(text).split(/\r?\n/).filter((l) => l.trim());
  const errors = [];
  if (lines.length < 2) {
    return { svgSections: existing, success: 0, failed: 0, errors: [{ row: 0, message: 'No data rows.' }] };
  }

  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
  const secIdx = headers.indexOf('section_id');
  const keyIdx = headers.indexOf('parameter_data_key');
  if (secIdx === -1 || keyIdx === -1) {
    return {
      svgSections: existing, success: 0, failed: 0,
      errors: [{ row: 1, message: 'Missing required columns: section_id, parameter_data_key.' }],
    };
  }

  const validSections = new Set(templateSections.map((s) => s.id));
  const keyToId = new Map(allParams.map((p) => [String(p.dataKey).toLowerCase(), p.id]));

  // Start from a mutable copy of existing assignments.
  const bySection = new Map(existing.map((s) => [s.sectionId, new Set(s.parameterIds ?? [])]));

  let success = 0;
  let failed = 0;

  lines.slice(1).forEach((line, i) => {
    const rowNum = i + 2; // 1-based, +1 for header
    const cells = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
    const sectionId = cells[secIdx] ?? '';
    const dataKey = cells[keyIdx] ?? '';

    if (!sectionId || !dataKey) {
      failed += 1; errors.push({ row: rowNum, message: 'Empty section_id or parameter_data_key.' }); return;
    }
    if (!validSections.has(sectionId)) {
      failed += 1; errors.push({ row: rowNum, message: `Unknown region "${sectionId}".` }); return;
    }
    const paramId = keyToId.get(dataKey.toLowerCase());
    if (!paramId) {
      failed += 1; errors.push({ row: rowNum, message: `Unknown parameter "${dataKey}".` }); return;
    }

    if (!bySection.has(sectionId)) bySection.set(sectionId, new Set());
    bySection.get(sectionId).add(paramId);
    success += 1;
  });

  const svgSections = [...bySection.entries()]
    .map(([sectionId, ids]) => ({ sectionId, parameterIds: [...ids] }))
    .filter((s) => s.parameterIds.length > 0);

  return { svgSections, success, failed, errors };
}

/**
 * Build a sample CSV for the chosen template: one row per region, paired with
 * a real parameter dataKey when available so the download is ready to edit.
 */
export function buildSampleCsv(templateSections, allParams) {
  const header = 'section_id,parameter_data_key';
  const sampleKey = allParams[0]?.dataKey ?? 'furnace_temp_c';
  const rows = templateSections.map((s) => `${s.id},${sampleKey}`);
  return [header, ...rows].join('\n');
}
